import React, { Fragment, useMemo } from 'react';
import classNames from 'classnames';
import bagBlue from '../../../../images/bag-blue.png';
import bagPink from '../../../../images/bag-pink.png';
import TechBoxImage from '../../../../images/tech-box.png';
import UnlimitedBadgeIcon from '../../../../images/unlimited-badge-figma.svg';
import CheckSvg from '../../../../global/SvgComponents/Check_svg';
import {
  SUBSCRIPTION_PAYMENT_TYPES,
  getOrderSubtotal,
} from '../../../../utils/subscriptions';
import {
  getFilteredMembershipItems,
  MEGA_KIT_MEMBERSHIP_ORDER_ITEMS,
} from '../../../../utils/membership';
import { useSubscription } from '../../SubscriptionProvider';

import './SubscriptionPaymentReview.scss';

const ThreeBagsImage = () => (
  <div className="SubscriptionPaymentReview__three-bags">
    <div className="SubscriptionPaymentReview__three-bags-inner">
      <div className="SubscriptionPaymentReview__three-bags-bag SubscriptionPaymentReview__three-bags-bag--left">
        <img src={bagBlue} alt="" />
      </div>
      <div className="SubscriptionPaymentReview__three-bags-bag SubscriptionPaymentReview__three-bags-bag--center">
        <img src={bagPink} alt="" />
      </div>
      <div className="SubscriptionPaymentReview__three-bags-bag SubscriptionPaymentReview__three-bags-bag--right">
        <img src={bagBlue} alt="" />
      </div>
    </div>
  </div>
);

const FourBagsImage = () => (
  <div className="SubscriptionPaymentReview__four-bags">
    <div className="SubscriptionPaymentReview__four-bags-inner">
      <div className="SubscriptionPaymentReview__four-bags-bag SubscriptionPaymentReview__four-bags-bag--1">
        <img src={bagBlue} alt="" />
      </div>
      <div className="SubscriptionPaymentReview__four-bags-bag SubscriptionPaymentReview__four-bags-bag--2">
        <img src={bagPink} alt="" />
      </div>
      <div className="SubscriptionPaymentReview__four-bags-bag SubscriptionPaymentReview__four-bags-bag--3">
        <img src={bagBlue} alt="" />
      </div>
      <div className="SubscriptionPaymentReview__four-bags-bag SubscriptionPaymentReview__four-bags-bag--4">
        <img src={bagPink} alt="" />
      </div>
    </div>
  </div>
);

const STARTER_KIT_ITEMS_UNLIMITED = [
  {
    key: 'bags',
    image: bagBlue,
    imageAlt: 'Take Back Bags',
    title: '3 Bags',
    description: (
      <>
        Clean out up to 45 lbs of
        <br />
        clothing and more
      </>
    ),
    imageContain: false,
  },
  {
    key: 'techbox',
    image: TechBoxImage,
    imageAlt: 'Tech Box',
    title: '1 Tech Box',
    description: (
      <>
        Clean out up to 10 lb of small
        <br />
        personal electronics.
      </>
    ),
    imageContain: true,
  },
];

const MEGA_KIT_ITEMS = [
  {
    key: 'bags',
    image: bagBlue,
    imageAlt: 'Take Back Bags',
    title: '4 Bags',
    description: (
      <>
        Clean out up to 60 lbs of
        <br />
        clothing and more
      </>
    ),
    imageContain: false,
  },
  {
    key: 'techbox',
    image: TechBoxImage,
    imageAlt: 'Tech Box',
    title: '1 Tech Box',
    description: (
      <>
        Clean out up to 10 lb of small
        <br />
        personal electronics.
      </>
    ),
    imageContain: true,
  },
];

const MEMBERSHIP_BENEFITS = [
  'Unlimited Take Back Bags',
  'Unlimited Tech boxes',
  'Free shipping, always',
  'Milestone jackpots and bonuses',
  'Cash out for gift cards \u2014 anytime',
];

const MEGA_KIT_BENEFITS = [
  '4 Take Back Bags',
  '1 Tech box',
  'Free shipping, always',
  'Milestone jackpots and bonuses',
  'Cash out for gift cards — anytime',
];

const KitCardVisual = ({ item, isMegaKitOrder }) => {
  if (item.key === 'bags' && !isMegaKitOrder) {
    return <ThreeBagsImage />;
  }
  if (item.key === 'bags' && isMegaKitOrder) {
    return <FourBagsImage />;
  }
  return <img src={item.image} alt={item.imageAlt} />;
};

const SubscriptionPaymentReview = ({ isMegaKit = false, onCheckout, checkoutDisabled = false }) => {
  const { subscriptionPlan, productPrices } = useSubscription();
  const { value: subscriptionValue, interval: subscriptionInterval = 'yr' } = subscriptionPlan || {};

  const membershipDisplayPrice = typeof subscriptionValue === 'number' && subscriptionValue >= 0
    ? subscriptionValue
    : 68;

  const membershipOneTimeIds = useMemo(
    () => getFilteredMembershipItems(SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME).map(m => m.id),
    [],
  );

  const megaKitSubtotal = useMemo(
    () => getOrderSubtotal(
      MEGA_KIT_MEMBERSHIP_ORDER_ITEMS,
      productPrices,
      membershipOneTimeIds,
    ),
    [productPrices, membershipOneTimeIds],
  );

  const megaDisplayPrice = megaKitSubtotal > 0 ? megaKitSubtotal : 98;
  const membershipFooterPrice = subscriptionInterval === 'yr'
    ? `$${membershipDisplayPrice} / year`
    : `$${membershipDisplayPrice} / ${subscriptionInterval}`;
  const megaFooterPrice = `$${megaDisplayPrice} one-time`;

  const kitItems = isMegaKit ? MEGA_KIT_ITEMS : STARTER_KIT_ITEMS_UNLIMITED;
  const title = isMegaKit ? 'Your Mega Kit' : 'Your Member Starter Kit';

  return (
    <div className="SubscriptionPaymentReview">
      <h2 className="SubscriptionPaymentReview__title">{title}</h2>

      <div className="SubscriptionPaymentReview__row">
        <div className="SubscriptionPaymentReview__kit">
          {kitItems.map((item, index) => (
            <Fragment key={item.key}>
              {index > 0 && <div className="SubscriptionPaymentReview__kit-divider" />}
              <div className="SubscriptionPaymentReview__kit-item">
                <div className="SubscriptionPaymentReview__kit-card">
                  <div
                    className={`SubscriptionPaymentReview__kit-card-image${item.imageContain ? ' SubscriptionPaymentReview__kit-card-image--contain' : ''}${item.key === 'bags' && !isMegaKit ? ' SubscriptionPaymentReview__kit-card-image--three-bags' : ''}${item.key === 'bags' && isMegaKit ? ' SubscriptionPaymentReview__kit-card-image--four-bags' : ''}`}
                  >
                    <KitCardVisual item={item} isMegaKitOrder={isMegaKit} />
                  </div>
                  <div className="SubscriptionPaymentReview__kit-card-text">
                    <p className="SubscriptionPaymentReview__kit-card-title">{item.title}</p>
                    <p className="SubscriptionPaymentReview__kit-card-desc">{item.description}</p>
                  </div>
                </div>
              </div>
            </Fragment>
          ))}
        </div>

        {!isMegaKit && (
          <div className="SubscriptionPaymentReview__membership">
            <div className="SubscriptionPaymentReview__membership-top">
              <p className="SubscriptionPaymentReview__membership-title">
                Trashie Unlimited
                {' '}
                <span className="SubscriptionPaymentReview__membership-title-line2">
                  Annual Membership
                </span>
              </p>
              <ul className="SubscriptionPaymentReview__membership-list">
                {MEMBERSHIP_BENEFITS.map((benefit) => (
                  <li key={benefit} className="SubscriptionPaymentReview__membership-item">
                    <span className="SubscriptionPaymentReview__check" aria-hidden="true">
                      <CheckSvg stroke="#FF4286" strokeWidth={1.25} />
                    </span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="SubscriptionPaymentReview__membership-breakeven">
              {'\uD83E\uDE77 Membership pays for itself at bag 10!\uD83E\uDE77'}
            </div>
          </div>
        )}

        {isMegaKit && (
          <div className="SubscriptionPaymentReview__membership SubscriptionPaymentReview__membership--mega">
            <div className="SubscriptionPaymentReview__membership-top">
              <p className="SubscriptionPaymentReview__membership-title">
                Your Mega Kit
              </p>
              <ul className="SubscriptionPaymentReview__membership-list">
                {MEGA_KIT_BENEFITS.map((benefit) => (
                  <li key={benefit} className="SubscriptionPaymentReview__membership-item">
                    <span className="SubscriptionPaymentReview__check" aria-hidden="true">
                      <CheckSvg stroke="#FF4286" strokeWidth={1.25} />
                    </span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      <div className="SubscriptionPaymentReview__divider" />

      <div className="SubscriptionPaymentReview__footer">
        <div className="SubscriptionPaymentReview__footer-left">
          <div className="SubscriptionPaymentReview__footer-brand">
            <span className="SubscriptionPaymentReview__footer-icon">
              <img src={UnlimitedBadgeIcon} alt="" />
            </span>
            <span className="SubscriptionPaymentReview__footer-label">
              {isMegaKit ? 'The Mega Kit' : 'Trashie Unlimited'}
            </span>
          </div>
          <span className="SubscriptionPaymentReview__footer-price">
            {isMegaKit ? megaFooterPrice : membershipFooterPrice}
          </span>
        </div>
        <button
          type="button"
          className={classNames(
            'SubscriptionPaymentReview__checkout',
            'ds20Btn',
            'ds20Btn--primary',
          )}
          onClick={onCheckout}
          disabled={checkoutDisabled}
        >
          Checkout Now &rarr;
        </button>
      </div>
    </div>
  );
};

export default SubscriptionPaymentReview;
